import { FC, ReactNode, useEffect, useRef } from 'react';
import { PanInfo } from 'motion/react';
import * as S from './styles';

const SWIPE_DISTANCE_PX = 80;
const SWIPE_VELOCITY_PX_PER_S = 500;
/** Scroll-down past this delta advances, matching swipe-up / TikTok-style feeds */
const WHEEL_DELTA_PX = 40;

export const isSwipeUp = ({ offset, velocity }: Pick<PanInfo, 'offset' | 'velocity'>) =>
  offset.y < -SWIPE_DISTANCE_PX || velocity.y < -SWIPE_VELOCITY_PX_PER_S;

const slideTransition = { type: 'tween', ease: [0.32, 0.72, 0, 1], duration: 0.4 } as const;

type SwipeCardProps = {
  canSwipe: boolean;
  onSwipeUp: () => void;
  children: ReactNode;
};

/** A full-screen card that follows the finger upwards and slides out, TikTok-style. */
export const SwipeCard: FC<SwipeCardProps> = ({ canSwipe, onSwipeUp, children }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const onSwipeUpRef = useRef(onSwipeUp);
  onSwipeUpRef.current = onSwipeUp;

  // Mouse-wheel / trackpad scroll-down advances when a Continue swipe is available
  // (narrow desktop and tablet-with-mouse on the mobile-width layout).
  useEffect(() => {
    const node = cardRef.current;
    if (!node || !canSwipe) return undefined;

    const onWheel = (event: WheelEvent) => {
      if (event.deltaY <= WHEEL_DELTA_PX) return;
      event.preventDefault();
      onSwipeUpRef.current();
    };

    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [canSwipe]);

  return (
    <S.Card
      ref={cardRef}
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '-100%' }}
      transition={slideTransition}
      drag={canSwipe ? 'y' : false}
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={{ top: 0.7, bottom: 0.05 }}
      dragSnapToOrigin
      onDragEnd={(_, info) => {
        if (isSwipeUp(info)) onSwipeUp();
      }}
      data-testid="blob-swipe-card"
    >
      {children}
    </S.Card>
  );
};
